import React, { useState } from 'react';
import axios from 'axios';

const INITIAL = {
  ticker: '',
  sectorBucket: '',
  strike: '',
  expirationDate: '',
  contracts: '1',
  premiumExpected: '',
  premiumActual: '',
  deltaAtOpen: '',
  ivRankAtOpen: '',
  decisionType: 'SYSTEM_ALIGNED',
  overrideReason: '',
  regimeFlagAtOpen: 'SIDEWAYS',
};

const SECTORS = ['FINTECH', 'TECH', 'CRYPTO_ADJ', 'ENERGY', 'RETAIL', 'OTHER'];
const REGIMES = ['UPTREND', 'SIDEWAYS', 'DOWNTREND', 'VOLATILE'];
const DECISION_TYPES = [
  { value: 'SYSTEM_ALIGNED', label: 'System aligned' },
  { value: 'SYSTEM_OVERRIDE', label: 'System override' },
  { value: 'FORCED', label: 'Forced (no-action override)' },
  { value: 'DEFERRED', label: 'Deferred' },
];

export default function AddTradeForm({ onClose, onSaved }) {
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  const needsReason = form.decisionType === 'SYSTEM_OVERRIDE' || form.decisionType === 'FORCED';

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!form.ticker || !form.strike || !form.expirationDate || !form.premiumActual) {
      setError('Ticker, strike, expiration, and actual premium are required.');
      return;
    }
    if (needsReason && !form.overrideReason.trim()) {
      setError('A reason is required when overriding or forcing a trade.');
      return;
    }

    try {
      setSaving(true);

      // 1. Create the position
      const capitalAtRisk = parseFloat(form.strike) * 100 * parseInt(form.contracts, 10);
      const premiumCollected = parseFloat(form.premiumActual) * 100 * parseInt(form.contracts, 10);

      const posRes = await axios.post('/api/positions', {
        ticker: form.ticker.toUpperCase(),
        sectorBucket: form.sectorBucket || null,
        totalCapitalAtRisk: capitalAtRisk,
        totalPremiumCollected: premiumCollected,
        regimeFlagAtOpen: form.regimeFlagAtOpen,
        currentRegimeFlag: form.regimeFlagAtOpen,
        ruleSetId: 'a0000000-0000-0000-0000-000000000001',
      });

      const positionId = posRes.data.position_id;

      // 2. Create the leg
      await axios.post(`/api/positions/${positionId}/legs`, {
        legType: 'SHORT_PUT',
        strike: parseFloat(form.strike),
        expirationDate: form.expirationDate,
        contracts: parseInt(form.contracts, 10),
        dteAtOpen: Math.ceil((new Date(form.expirationDate) - new Date()) / (1000 * 60 * 60 * 24)),
        premiumExpected: form.premiumExpected ? parseFloat(form.premiumExpected) : null,
        premiumActual: parseFloat(form.premiumActual),
        deltaAtOpen: form.deltaAtOpen ? parseFloat(form.deltaAtOpen) : null,
        ivRankAtOpen: form.ivRankAtOpen ? parseFloat(form.ivRankAtOpen) : null,
        decisionType: form.decisionType,
        overrideReason: needsReason ? form.overrideReason : null,
      });

      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="form-overlay" onClick={onClose}>
      <div className="form-panel" onClick={e => e.stopPropagation()}>
        <div className="form-header">
          <span className="form-title">Open short put</span>
          <button className="form-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>

          <div className="form-section-label">Underlying</div>
          <div className="form-row-2">
            <div className="form-group">
              <label>Ticker *</label>
              <input
                type="text"
                placeholder="SOFI"
                value={form.ticker}
                onChange={e => set('ticker', e.target.value.toUpperCase())}
                maxLength={6}
              />
            </div>
            <div className="form-group">
              <label>Sector</label>
              <select value={form.sectorBucket} onChange={e => set('sectorBucket', e.target.value)}>
                <option value="">— select —</option>
                {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>Regime at open *</label>
              <select value={form.regimeFlagAtOpen} onChange={e => set('regimeFlagAtOpen', e.target.value)}>
                {REGIMES.map(r => <option key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()}</option>)}
              </select>
            </div>
          </div>

          <div className="form-section-label">Contract details</div>
          <div className="form-row-3">
            <div className="form-group">
              <label>Strike *</label>
              <input
                type="number"
                placeholder="13.00"
                step="0.50"
                min="0"
                value={form.strike}
                onChange={e => set('strike', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Expiration *</label>
              <input
                type="date"
                value={form.expirationDate}
                onChange={e => set('expirationDate', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Contracts</label>
              <input
                type="number"
                min="1"
                step="1"
                value={form.contracts}
                onChange={e => set('contracts', e.target.value)}
              />
            </div>
          </div>

          <div className="form-section-label">Fill</div>
          <div className="form-row-3">
            <div className="form-group">
              <label>Premium expected</label>
              <input
                type="number"
                placeholder="0.44"
                step="0.01"
                min="0"
                value={form.premiumExpected}
                onChange={e => set('premiumExpected', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Premium actual *</label>
              <input
                type="number"
                placeholder="0.41"
                step="0.01"
                min="0"
                value={form.premiumActual}
                onChange={e => set('premiumActual', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Delta at open</label>
              <input
                type="number"
                placeholder="0.26"
                step="0.01"
                min="0"
                max="1"
                value={form.deltaAtOpen}
                onChange={e => set('deltaAtOpen', e.target.value)}
              />
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>IV rank</label>
              <input
                type="number"
                placeholder="58"
                step="1"
                min="0"
                max="100"
                value={form.ivRankAtOpen}
                onChange={e => set('ivRankAtOpen', e.target.value)}
              />
            </div>
          </div>

          <div className="form-section-label">Decision</div>
          <div className="form-group">
            <label>Decision type *</label>
            <select value={form.decisionType} onChange={e => set('decisionType', e.target.value)}>
              {DECISION_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>

          {needsReason && (
            <div className="form-group">
              <label>Reason for override *</label>
              <textarea
                rows={3}
                placeholder="Explain why you're overriding the system recommendation..."
                value={form.overrideReason}
                onChange={e => set('overrideReason', e.target.value)}
              />
            </div>
          )}

          {error && <div className="form-error">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Saving...' : 'Open position'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
